import { ATTACHMENT_ACCEPTED_MIME, ATTACHMENT_MAX_BYTES } from '@/types/cgt'

/**
 * Pure rules behind the multi-file Attachment field (z8nrz7dp7p), kept out of
 * the component so vitest can reach them — it runs `.ts` only.
 *
 * The server re-checks both (type and size) and stays the
 * authority; this is what lets the filler see WHY a file was refused before an
 * upload is even attempted.
 */

export interface PickedFile {
  name: string
  type: string
  size: number
}

export interface FileRejection {
  fileName: string
  reason: string
}

function formatMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`
}

/**
 * Split a picked batch into the files to upload and the files refused, with a
 * reason each. There is no limit on how many files a field holds, so only type
 * and size are judged, and a bad file never takes the good ones down with it.
 */
export function triageFiles<F extends PickedFile>(
  picked: readonly F[],
): { accepted: F[]; rejected: FileRejection[] } {
  const accepted: F[] = []
  const rejected: FileRejection[] = []
  for (const file of picked) {
    if (!ATTACHMENT_ACCEPTED_MIME.includes(file.type)) {
      rejected.push({ fileName: file.name, reason: 'Only PDF, JPEG and PNG files can be attached.' })
      continue
    }
    if (file.size > ATTACHMENT_MAX_BYTES) {
      rejected.push({
        fileName: file.name,
        reason: `The file is larger than the ${formatMb(ATTACHMENT_MAX_BYTES)} limit.`,
      })
      continue
    }
    accepted.push(file)
  }
  return { accepted, rejected }
}
